import React, { useState, useEffect } from 'react';
import { Play, ExternalLink, Image, AlertCircle, X, Minimize2, Maximize2 } from 'lucide-react';
import Input from '@/components/ui/Input';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { MediaSource } from '@/types/media';
import { useTranslation } from '@/hooks';
import {
  getYouTubeThumbnail,
  isYouTubeUrl,
  getVideoPlatform,
  getYouTubeEmbedUrl,
  getVimeoEmbedUrl,
  isVimeoUrl,
  isDirectVideoUrl
} from '@/utils/youtube';

interface MediaAssetsSectionProps {
  backgroundImageUrl: string;
  trailerUrl: string;
  onBackgroundImageChange: (url: string) => void;
  onTrailerUrlChange: (url: string) => void;
  backgroundImageSource?: MediaSource;
  backgroundImageError?: string;
  trailerUrlError?: string;
}

const MediaAssetsSection: React.FC<MediaAssetsSectionProps> = ({
  backgroundImageUrl,
  trailerUrl,
  onBackgroundImageChange,
  onTrailerUrlChange,
  backgroundImageSource,
  backgroundImageError,
  trailerUrlError
}) => {
  const { t } = useTranslation();
  const [backgroundImageLoading, setBackgroundImageLoading] = useState(false);
  const [backgroundImageLoadError, setBackgroundImageLoadError] = useState(false);
  const [trailerThumbnail, setTrailerThumbnail] = useState<string | null>(null);
  const [trailerThumbnailError, setTrailerThumbnailError] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoSize, setVideoSize] = useState<'normal' | 'large'>('normal');

  // Update background image loading states
  useEffect(() => {
    if (backgroundImageUrl) {
      setBackgroundImageLoading(true);
      setBackgroundImageLoadError(false);
    } else {
      setBackgroundImageLoading(false);
      setBackgroundImageLoadError(false);
    }
  }, [backgroundImageUrl]);

  // Update trailer thumbnail when trailer URL changes
  useEffect(() => {
    if (trailerUrl) {
      setIsVideoPlaying(false); // Reset player state when URL changes
      if (isYouTubeUrl(trailerUrl)) {
        const thumbnail = getYouTubeThumbnail(trailerUrl);
        setTrailerThumbnail(thumbnail);
        setTrailerThumbnailError(false);
      } else {
        // For non-YouTube URLs (Vimeo, direct videos), we don't show thumbnails
        setTrailerThumbnail(null);
        setTrailerThumbnailError(false);
      }
    } else {
      setTrailerThumbnail(null);
      setTrailerThumbnailError(false);
      setIsVideoPlaying(false);
    }
  }, [trailerUrl]);

  const handleBackgroundImageLoad = () => {
    setBackgroundImageLoading(false);
    setBackgroundImageLoadError(false);
  };

  const handleBackgroundImageError = () => {
    setBackgroundImageLoading(false);
    setBackgroundImageLoadError(true);
  };

  const handleTrailerThumbnailError = () => {
    setTrailerThumbnailError(true);
  };

  const playVideo = () => {
    setIsVideoPlaying(true);
  };

  const stopVideo = () => {
    setIsVideoPlaying(false);
    setVideoSize('normal');
  };

  const toggleVideoSize = () => {
    setVideoSize(videoSize === 'normal' ? 'large' : 'normal');
  };

  const openTrailerExternally = () => {
    if (trailerUrl) {
      window.open(trailerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getEmbedUrl = (url: string): string | null => {
    if (isYouTubeUrl(url)) {
      return getYouTubeEmbedUrl(url);
    } else if (isVimeoUrl(url)) {
      return getVimeoEmbedUrl(url);
    }
    return null;
  };

  const canPlayInline = (url: string): boolean => {
    return isYouTubeUrl(url) || isVimeoUrl(url) || isDirectVideoUrl(url);
  };

  const getVideoPlatformIcon = (url: string) => {
    const platform = getVideoPlatform(url);
    switch (platform) {
      case 'youtube':
        return <Play className="h-8 w-8 text-red-600" />;
      case 'vimeo':
        return <Play className="h-8 w-8 text-blue-600" />;
      default:
        return <Play className="h-8 w-8 text-gray-600" />;
    }
  };

  const renderVideoPlayer = () => {
    if (!trailerUrl || !isVideoPlaying) return null;

    const embedUrl = getEmbedUrl(trailerUrl);
    const isLarge = videoSize === 'large';
    const playerHeight = isLarge ? 'h-64' : 'h-32';

    return (
      <div className={`relative ${playerHeight} w-full border border-gray-300 rounded-md overflow-hidden bg-black`}>
        {/* Video Player Controls */}
        <div className="absolute top-2 right-2 flex space-x-1 z-10">
          <button
            onClick={toggleVideoSize}
            className="bg-black bg-opacity-70 text-white p-1.5 rounded hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            aria-label={isLarge ? t.mediaAssets.minimizeVideo : t.mediaAssets.maximizeVideo}
          >
            {isLarge ? (
              <Minimize2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <button
            onClick={openTrailerExternally}
            className="bg-black bg-opacity-70 text-white p-1.5 rounded hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            aria-label={t.mediaAssets.openVideoInNewTab}
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            onClick={stopVideo}
            className="bg-black bg-opacity-70 text-white p-1.5 rounded hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            aria-label={t.mediaAssets.closeVideoPlayer}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Embedded Video */}
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title={t.mediaAssets.trailerPreview}
          />
        ) : isDirectVideoUrl(trailerUrl) ? (
          <video
            className="w-full h-full"
            controls
            preload="metadata"
          >
            <source src={trailerUrl} type="video/mp4" />
            {t.mediaAssets.videoNotSupported}
          </video>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm">{t.mediaAssets.videoFormatNotSupported}</p>
              <button
                onClick={openTrailerExternally}
                className="mt-2 text-blue-300 hover:text-blue-100 underline"
              >
                {t.mediaAssets.openInNewTab}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            {backgroundImageSource ? (
              <ImageUrlUploadField
                label={t.mediaAssets.backgroundImageUrl}
                placeholder={t.mediaAssets.backgroundImagePlaceholder}
                value={backgroundImageUrl}
                onChange={onBackgroundImageChange}
                error={backgroundImageError}
                source={backgroundImageSource}
                showPreview={false}
              />
            ) : (
              <Input
                label={t.mediaAssets.backgroundImageUrl}
                type="url"
                placeholder={t.mediaAssets.backgroundImagePlaceholder}
                value={backgroundImageUrl}
                onChange={(e) => onBackgroundImageChange(e.target.value)}
                error={backgroundImageError}
              />
            )}

            {backgroundImageUrl && (
              <div className="space-y-1">
                <div className="relative h-32 w-full border border-gray-300 rounded-md overflow-hidden bg-gray-50" role="img" aria-label={t.mediaAssets.backgroundPreview}>
                  {backgroundImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center" role="status" aria-label={t.mediaAssets.loadingBackgroundImage}>
                      <div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}

                  {backgroundImageLoadError ? (
                    <div className="absolute inset-0 flex items-center justify-center text-red-500" role="alert" aria-label={t.mediaAssets.backgroundImageFailedToLoad}>
                      <div className="text-center">
                        <AlertCircle className="h-6 w-6 mx-auto mb-1" aria-hidden="true" />
                        <div className="text-xs">{t.mediaAssets.failedToLoad}</div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={backgroundImageUrl}
                      alt={t.mediaAssets.backgroundPreview}
                      className="w-full h-full object-cover"
                      onLoad={handleBackgroundImageLoad}
                      onError={handleBackgroundImageError}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Input
              label={t.mediaAssets.trailerUrl}
              type="url"
              placeholder={t.mediaAssets.trailerUrlPlaceholder}
              value={trailerUrl}
              onChange={(e) => onTrailerUrlChange(e.target.value)}
              error={trailerUrlError}
            />

            {trailerUrl && (
              <div className="space-y-1">
                {isVideoPlaying ? (
                  renderVideoPlayer()
                ) : (
                  <div
                    className="relative h-32 w-full border border-gray-300 rounded-md overflow-hidden bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onClick={canPlayInline(trailerUrl) ? playVideo : openTrailerExternally}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (canPlayInline(trailerUrl) ? playVideo() : openTrailerExternally())}
                    aria-label={canPlayInline(trailerUrl) ? t.mediaAssets.playVideoInline : `${t.mediaAssets.openTrailerInNewTab} - ${getVideoPlatform(trailerUrl) || t.mediaAssets.videoContent}`}
                  >
                    {trailerThumbnail && !trailerThumbnailError ? (
                      <div className="relative w-full h-full">
                        <img
                          src={trailerThumbnail}
                          alt={t.mediaAssets.trailerPreview}
                          className="w-full h-full object-cover"
                          onError={handleTrailerThumbnailError}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                          <div className="bg-white bg-opacity-90 rounded-full p-2">
                            <Play className="h-6 w-6 text-gray-800 fill-current" aria-hidden="true" />
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                          {canPlayInline(trailerUrl) ? (
                            <span>{t.mediaAssets.clickToPlay}</span>
                          ) : (
                            <>
                              <ExternalLink className="h-3 w-3 inline mr-1" aria-hidden="true" />
                              {getVideoPlatform(trailerUrl) || t.mediaAssets.videoContent}
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          {getVideoPlatformIcon(trailerUrl)}
                          <div className="text-xs mt-1">
                            {getVideoPlatform(trailerUrl) === 'youtube' ? t.mediaAssets.youtubeVideo :
                             getVideoPlatform(trailerUrl) === 'vimeo' ? t.mediaAssets.vimeoVideo : t.mediaAssets.videoTrailer}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {canPlayInline(trailerUrl) ? t.mediaAssets.clickToPlay : t.mediaAssets.clickToOpen}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {!backgroundImageUrl && !trailerUrl && (
          <div className="text-center py-8 text-gray-400">
            <div className="flex justify-center space-x-4 mb-2">
              <Image className="h-8 w-8" aria-hidden="true" />
              <Play className="h-8 w-8" aria-hidden="true" />
            </div>
            <p className="text-sm">{t.mediaAssets.emptyStateMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaAssetsSection;
